import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  fillApplicationErrorMessage,
  type FillOrgRef,
  type IntakeUploadResult,
  invalidateFillApplication,
  startForm,
} from '@/models/fillApplication'
import { DuplicateCustomerNotice } from './DuplicateCustomerNotice'
import { FillSectionCard } from './FillSectionCard'
import { FillTabHeader } from './FillTabHeader'
import { IntakeDropzone } from './IntakeDropzone'

interface StartViewProps {
  orgId: string
  /** Another active customer holding the DOT on file. */
  duplicateOf?: FillOrgRef | null
  onUploaded: (result: IntakeUploadResult) => void
}

export function StartView({ orgId, duplicateOf = null, onUploaded }: StartViewProps) {
  const queryClient = useQueryClient()
  const [skipping, setSkipping] = useState(false)

  async function skip() {
    setSkipping(true)
    try {
      await startForm(orgId)
      await invalidateFillApplication(queryClient, orgId)
    } catch (error) {
      toast.error(fillApplicationErrorMessage(error))
    } finally {
      setSkipping(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <FillTabHeader
        title="Documents"
        description="Drop what the customer sent. Cai reads the files and fills in what it can, then you check it and finish the rest."
      />
      {duplicateOf && <DuplicateCustomerNotice duplicate={duplicateOf} />}
      <FillSectionCard>
        <IntakeDropzone orgId={orgId} size="large" onUploaded={onUploaded} />
        <div>
          <Button
            type="button"
            variant="link"
            className="px-0"
            onClick={() => void skip()}
            loading={skipping ? 'Opening the form…' : undefined}
          >
            Skip, fill it in by hand
          </Button>
        </div>
      </FillSectionCard>
    </div>
  )
}
